
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { PlusCircle, Quote, User, PiggyBank, Camera } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Child, QuoteItem, getStoredChildren, storeQuotes, storeChildren } from '@/utils/storage';
import { logger } from '@/utils/logger';
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { resetZoom } from '@/utils/zoom-fix';

interface AddNewDialogProps {
  children?: React.ReactNode;
  onQuoteAdded?: (quote: QuoteItem) => void;
}

const AddNewDialog = ({ children, onQuoteAdded }: AddNewDialogProps) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('quote');
  
  // Quote-related state
  const [childrenData, setChildrenData] = useState<{ id: string; name: string }[]>([]);
  const [newQuote, setNewQuote] = useState<Omit<QuoteItem, 'id' | 'childName'>>({
    content: '',
    childId: '',
    date: new Date().toISOString().split('T')[0],
    context: ''
  });
  
  // Child-related state
  const [newChild, setNewChild] = useState<Omit<Child, 'id'>>({
    name: '',
    birthdate: '',
    photoUrl: ''
  });
  const [childPhoto, setChildPhoto] = useState<File | null>(null);
  const [childPhotoPreview, setChildPhotoPreview] = useState<string | null>(null);
  
  // Milestone-related state
  const [newMilestone, setNewMilestone] = useState({
    title: '',
    childId: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });

  useEffect(() => {
    const fetchChildren = async () => {
      const storedChildren = await getStoredChildren();
      if (storedChildren.length > 0) {
        setChildrenData(storedChildren.map(child => ({ id: child.id, name: child.name })));
      }
    };
    
    fetchChildren();
  }, []);

  const resetForms = () => {
    // Reset quote form
    setNewQuote({
      content: '',
      childId: '',
      date: new Date().toISOString().split('T')[0],
      context: ''
    });
    
    // Reset child form
    setNewChild({
      name: '',
      birthdate: '',
      photoUrl: ''
    });
    setChildPhoto(null);
    setChildPhotoPreview(null);
    
    // Reset milestone form
    setNewMilestone({
      title: '',
      childId: '',
      date: new Date().toISOString().split('T')[0],
      description: ''
    });
    
    // Reset to quote tab as default
    setActiveTab('quote');
  };

  const handleAddQuote = async () => {
    if (!newQuote.content || !newQuote.childId || !newQuote.date) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const newQuoteItem = await storeQuotes(newQuote);
      
      if (newQuoteItem && onQuoteAdded) {
        onQuoteAdded(newQuoteItem);
      }
      
      resetForms();
      setOpen(false);
      
      toast({
        title: "Quote added",
        description: "The quote has been saved successfully!"
      });
    } catch (error) {
      logger.error("Error adding quote:", error);
      toast({
        title: "Error",
        description: "Failed to add quote. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleAddChild = async () => {
    if (!newChild.name || !newChild.birthdate) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      const newChildItem = await storeChildren(newChild, childPhoto || undefined);

      if (newChildItem) {
        // Update the children data
        const updatedChildren = await getStoredChildren();
        setChildrenData(updatedChildren.map(child => ({ id: child.id, name: child.name })));
      }

      resetForms();
      setOpen(false);

      toast({
        title: "Child added",
        description: `${newChild.name} has been added successfully!`
      });
    } catch (error) {
      logger.error("Error adding child:", error);
      toast({
        title: "Error",
        description: "Failed to add child. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleAddMilestone = () => {
    if (!newMilestone.title || !newMilestone.childId || !newMilestone.date) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    // This is a placeholder since we don't have milestone storage yet
    toast({
      title: "Milestone feature coming soon",
      description: "The milestone feature is not yet implemented"
    });

    resetForms();
    setOpen(false);
  };

  const handleChildPhotoClick = async () => {
    try {
      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        const image = await CapacitorCamera.getPhoto({
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Prompt,
          quality: 80,
          width: 800,
          height: 800,
          allowEditing: true,
        });

        if (image.dataUrl) {
          setChildPhotoPreview(image.dataUrl);
          const response = await fetch(image.dataUrl);
          const blob = await response.blob();
          const file = new File([blob], 'child-photo.jpg', { type: 'image/jpeg' });
          setChildPhoto(file);
        }
      } else {
        const image = await CapacitorCamera.getPhoto({
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Photos,
          quality: 80,
          width: 800,
          height: 800,
          allowEditing: true,
        });

        if (image.dataUrl) {
          setChildPhotoPreview(image.dataUrl);
          const response = await fetch(image.dataUrl);
          const blob = await response.blob();
          const file = new File([blob], 'child-photo.jpg', { type: 'image/jpeg' });
          setChildPhoto(file);
        }
      }
    } catch (error) {
      if (error instanceof Error && error.message !== 'User cancelled photos app') {
        toast({
          title: "Photo Error",
          description: "Could not access photos. Please check permissions.",
          variant: "destructive"
        });
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) {
        resetForms();
        // Reset zoom when dialog closes to fix iOS zoom issues
        resetZoom();
      }
    }}>
      <DialogTrigger asChild>
        {children || (
          <Button className="rounded-full bg-primary text-white hover:bg-primary/90">
            <PlusCircle className="h-4 w-4 mr-1.5" />
            Add New
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] w-[95%] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New</DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mt-2">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="quote" className="flex items-center gap-1.5">
              <Quote className="h-3.5 w-3.5" />
              <span>Quote</span>
            </TabsTrigger>
            <TabsTrigger value="milestone" className="flex items-center gap-1.5">
              <PiggyBank className="h-3.5 w-3.5" />
              <span>Milestone</span>
            </TabsTrigger>
            <TabsTrigger value="child" className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              <span>Child</span>
            </TabsTrigger>
          </TabsList>
          
          {/* Quote Form */}
          <TabsContent value="quote" className="mt-4">
            <div className="grid gap-4 py-2">
              <div className="grid gap-2">
                <Label htmlFor="child">Child</Label>
                <Select 
                  onValueChange={(value) => setNewQuote({...newQuote, childId: value})}
                  value={newQuote.childId}
                >
                  <SelectTrigger className="rounded-md">
                    <SelectValue placeholder="Select a child" />
                  </SelectTrigger>
                  <SelectContent>
                    {childrenData.map(child => (
                      <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="quote">Quote</Label>
                <Textarea 
                  id="quote" 
                  placeholder="What did they say?"
                  value={newQuote.content}
                  onChange={(e) => setNewQuote({...newQuote, content: e.target.value})}
                  className="min-h-[80px] rounded-md max-h-[150px] overflow-y-auto"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="date">Date</Label>
                <Input 
                  id="date" 
                  type="date" 
                  value={newQuote.date}
                  onChange={(e) => setNewQuote({...newQuote, date: e.target.value})}
                  className="rounded-md"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="context">Context (optional)</Label>
                <Textarea 
                  id="context" 
                  placeholder="What was happening when they said this?"
                  value={newQuote.context || ''}
                  onChange={(e) => setNewQuote({...newQuote, context: e.target.value})}
                  className="rounded-md max-h-[100px] overflow-y-auto"
                />
              </div>
              <Button onClick={handleAddQuote} className="mt-2 rounded-full">Save Quote</Button>
            </div>
          </TabsContent>
          
          {/* Milestone Form */}
          <TabsContent value="milestone" className="mt-4">
            <div className="grid gap-4 py-2">
              <div className="grid gap-2">
                <Label htmlFor="milestone-child">Child</Label>
                <Select 
                  onValueChange={(value) => setNewMilestone({...newMilestone, childId: value})}
                  value={newMilestone.childId}
                >
                  <SelectTrigger className="rounded-md">
                    <SelectValue placeholder="Select a child" />
                  </SelectTrigger>
                  <SelectContent>
                    {childrenData.map(child => (
                      <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="milestone-title">Title</Label>
                <Input 
                  id="milestone-title" 
                  placeholder="First steps, First word, etc."
                  value={newMilestone.title}
                  onChange={(e) => setNewMilestone({...newMilestone, title: e.target.value})}
                  className="rounded-md"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="milestone-date">Date</Label>
                <Input 
                  id="milestone-date" 
                  type="date" 
                  value={newMilestone.date}
                  onChange={(e) => setNewMilestone({...newMilestone, date: e.target.value})}
                  className="rounded-md"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="milestone-description">Description (optional)</Label>
                <Textarea 
                  id="milestone-description" 
                  placeholder="Additional details about this milestone"
                  value={newMilestone.description || ''}
                  onChange={(e) => setNewMilestone({...newMilestone, description: e.target.value})}
                  className="rounded-md max-h-[100px] overflow-y-auto"
                />
              </div>
              <Button onClick={handleAddMilestone} className="mt-2 rounded-full">Save Milestone</Button>
            </div>
          </TabsContent>
          
          {/* Child Form */}
          <TabsContent value="child" className="mt-4">
            <div className="grid gap-4 py-2">
              <div className="flex justify-center mb-2">
                <div
                  onClick={handleChildPhotoClick}
                  className="relative cursor-pointer"
                >
                  <Avatar className="h-24 w-24">
                    {childPhotoPreview ? (
                      <AvatarImage src={childPhotoPreview} alt="Preview" />
                    ) : (
                      <AvatarFallback className="bg-gray-200">
                        <User className="h-12 w-12 text-gray-400" />
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="absolute bottom-0 right-0 bg-primary rounded-full p-1">
                    <Camera className="h-4 w-4 text-white" />
                  </div>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="child-name">Name</Label>
                <Input
                  id="child-name"
                  placeholder="Child's name"
                  value={newChild.name}
                  onChange={(e) => setNewChild({...newChild, name: e.target.value})}
                  className="rounded-md"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="child-birthdate">Birthdate</Label>
                <Input
                  id="child-birthdate"
                  type="date"
                  value={newChild.birthdate}
                  onChange={(e) => setNewChild({...newChild, birthdate: e.target.value})}
                  className="rounded-md"
                />
              </div>
              <Button onClick={handleAddChild} className="mt-2 rounded-full">Save Child</Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AddNewDialog;
